import { FetchProvider } from "@/providers/fetch";
import { CheckoutProvider } from "@/providers/useCheckout";
import { LoginActionsProvider } from "@/providers/useLoginActions";
import { RiotBarProvider } from "@/providers/useRiotBar";
import { GatsbyBrowser, navigate, WrapPageElementBrowserArgs } from "gatsby";
import { I18NextContext } from "gatsby-plugin-react-i18next/dist/types";
import React from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { NewRelicScript } from "./providers/NewRelicProvider";
import { useSimpleI18n } from "./utils/rioti18n";
import { REDEEM_ONLY_LOCALES } from "./variables";
import { XSSOProvider } from "@/providers/useXSSOAuth";
import queryClient from "./utils/clientProvider";

export const wrapRootElement: GatsbyBrowser["wrapRootElement"] = ({ element }) => (
  <FetchProvider>
    <NewRelicScript />
    <QueryClientProvider client={queryClient}>
      <CheckoutProvider>
        <LoginActionsProvider>
          <RiotBarProvider>
            <XSSOProvider>{element}</XSSOProvider>
          </RiotBarProvider>
        </LoginActionsProvider>
      </CheckoutProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </FetchProvider>
);

const UnsupportedLanguageRedirect = ({
  children,
  locale,
}: {
  children: React.ReactNode | React.ReactNode[];
  locale?: string;
}) => {
  const { language } = useSimpleI18n(locale);

  // Redirect to Redeem when user navigates a redeem only language
  React.useEffect(() => {
    REDEEM_ONLY_LOCALES.includes(language) && navigate(`/${language}/redeem`, { replace: true });
  }, [language]);

  return <>{children}</>;
};

export const wrapPageElement: GatsbyBrowser["wrapPageElement"] = ({
  element,
  props,
}: WrapPageElementBrowserArgs<unknown, Record<string, unknown> & { i18n?: I18NextContext }>) => {
  const { location, pageContext: { i18n, pageWithoutLanguage } = {} } = props;

  const exclusions = ["/redeem", "/account/name-change-token", "/redirect", "/success"];
  const isExcluded = exclusions.some((exclusion) => location?.pathname.indexOf(exclusion) !== -1);

  if (isExcluded) {
    return element;
  }

  return (
    <UnsupportedLanguageRedirect locale={pageWithoutLanguage ? undefined : i18n?.language}>
      {element}
    </UnsupportedLanguageRedirect>
  );
};
